import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import api from "@/lib/api";
import { Waypoints, ZoomIn, ZoomOut, Maximize2, Search, Crosshair, X } from "lucide-react";

const TRACK_COLORS = { foundations: "#ffd873", status: "#7fe3e0", consumer: "#ff9d6c" };
const TYPE_COLORS = { case: "#c4a9ff", term: "#8fb6ff", state: "#8ff0a0", template: "#ffcf8f", ai: "#ffd873", teaching: "#ff7ac6", statute: "#ffb3b3", portal: "#7fe3d6", code: "#ffd27f", deck: "#b6ff9d", link: "#c9c9d6" };
const CX = 560, CY = 520, INNER_R = 250, OUTER_R = 460;

function starPolygon(spikes, outerR, innerR) {
  let rot = (Math.PI / 2) * 3;
  const step = Math.PI / spikes;
  let pts = "";
  for (let i = 0; i < spikes; i++) {
    pts += `${Math.cos(rot) * outerR},${Math.sin(rot) * outerR} `;
    rot += step;
    pts += `${Math.cos(rot) * innerR},${Math.sin(rot) * innerR} `;
    rot += step;
  }
  return pts.trim();
}

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export default function RabbitHoleMap() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [graph, setGraph] = useState({ nodes: [], edges: [] });
  const [view, setView] = useState({ tx: 0, ty: 0, scale: 1 });
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);
  const containerRef = useRef(null);
  const pointers = useRef(new Map());
  const pinch = useRef(null);
  const moved = useRef(0);
  const [flying, setFlying] = useState(false);
  const flyTimers = useRef([]);
  const [disabled, setDisabled] = useState(new Set());
  const [search, setSearch] = useState("");
  const [focusMode, setFocusMode] = useState(false);
  const [focused, setFocused] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [hoverExtra, setHoverExtra] = useState(null);

  const toggleExpand = (id) =>
    setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const legendKey = (n) => {
    if (n.type === "course") return n.track || "other";
    if (["case", "term", "teaching", "statute", "portal", "code", "deck"].includes(n.type)) return n.type;
    return "other";
  };
  const isVisible = (n) => !disabled.has(legendKey(n));
  const toggleFilter = (key) =>
    setDisabled((d) => { const n = new Set(d); n.has(key) ? n.delete(key) : n.add(key); return n; });

  useEffect(() => {
    api.get("/map").then((r) => setGraph(r.data)).catch(() => {});
  }, []);

  const bgStars = useMemo(
    () => Array.from({ length: 150 }, () => ({
      x: Math.random() * 100, y: Math.random() * 100,
      r: Math.random() * 1.3 + 0.25, d: (Math.random() * 5).toFixed(2),
      o: Math.random() * 0.6 + 0.25,
    })), []);

  const layout = useMemo(() => {
    const courses = graph.nodes.filter((n) => n.type === "course");
    const pos = {};
    const N = courses.length || 1;
    courses.forEach((c, i) => {
      const a = (i / N) * Math.PI * 2 - Math.PI / 2;
      pos[c.id] = { ...c, x: CX + INNER_R * Math.cos(a), y: CY + INNER_R * Math.sin(a), angle: a };
    });
    const parentAngle = {};
    graph.edges.forEach((e) => {
      if (pos[e.source] && !(e.target in parentAngle)) parentAngle[e.target] = pos[e.source].angle;
    });
    const bucket = {};
    graph.nodes.filter((n) => n.type !== "course").forEach((n) => {
      const a = parentAngle[n.id] ?? 0;
      const key = a.toFixed(2);
      bucket[key] = bucket[key] || [];
      bucket[key].push(n);
    });
    Object.entries(bucket).forEach(([key, arr]) => {
      const base = parseFloat(key);
      arr.forEach((n, j) => {
        const spread = (j - (arr.length - 1) / 2) * 0.16;
        const a = base + spread;
        const r = OUTER_R + (j % 2) * 46;
        pos[n.id] = { ...n, x: CX + r * Math.cos(a), y: CY + r * Math.sin(a) };
      });
    });
    return pos;
  }, [graph]);

  const rect = () => containerRef.current?.getBoundingClientRect() || { left: 0, top: 0, width: 1, height: 1 };

  const zoomAbout = (fx, fy, factor) => {
    setView((v) => {
      const s = clamp(v.scale * factor, 0.4, 4);
      const f = s / v.scale;
      return { scale: s, tx: fx - (fx - v.tx) * f, ty: fy - (fy - v.ty) * f };
    });
  };

  const onPointerDown = (e) => {
    moved.current = 0;
    if (flying) stopFly();
    setSelected(null);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    const prev = pointers.current.get(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const r = rect();
    if (pointers.current.size === 1) {
      const dx = e.clientX - prev.x, dy = e.clientY - prev.y;
      moved.current += Math.abs(dx) + Math.abs(dy);
      setView((v) => ({ ...v, tx: v.tx + dx, ty: v.ty + dy }));
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      moved.current += 20;
      if (pinch.current && pinch.current.dist > 0) {
        const factor = dist / pinch.current.dist;
        const fx = mx - r.left, fy = my - r.top;
        zoomAbout(fx, fy, factor);
        const pdx = mx - pinch.current.mx, pdy = my - pinch.current.my;
        if (pdx || pdy) setView((v) => ({ ...v, tx: v.tx + pdx, ty: v.ty + pdy }));
      }
      pinch.current = { dist, mx, my };
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  };

  const onWheel = (e) => {
    const r = rect();
    zoomAbout(e.clientX - r.left, e.clientY - r.top, e.deltaY < 0 ? 1.12 : 0.89);
  };

  const btnZoom = (factor) => { const r = rect(); zoomAbout(r.width / 2, r.height / 2, factor); };
  const reset = () => setView({ tx: 0, ty: 0, scale: 1 });

  const centerOn = (p, scale) => {
    const r = rect();
    const f = Math.min(r.width / 1120, r.height / 1040);
    const ox = (r.width - 1120 * f) / 2, oy = (r.height - 1040 * f) / 2;
    const bx = ox + p.x * f, by = oy + p.y * f;
    setView({ scale, tx: r.width / 2 - scale * bx, ty: r.height / 2 - scale * by });
  };

  const stopFly = () => {
    flyTimers.current.forEach(clearTimeout);
    flyTimers.current = [];
    setFlying(false);
  };
  useEffect(() => () => flyTimers.current.forEach(clearTimeout), []);

  const playTrack = (track) => {
    stopFly();
    const seq = graph.nodes.filter((n) => n.type === "course" && n.track === track);
    if (!seq.length) return;
    setFlying(true);
    seq.forEach((n, i) => {
      flyTimers.current.push(setTimeout(() => {
        const p = layout[n.id];
        if (p) { setSelected(n.id); centerOn(p, 1.9); }
        if (i === seq.length - 1) {
          flyTimers.current.push(setTimeout(() => { setFlying(false); }, 2600));
        }
      }, i * 2800));
    });
  };
  const tracks = useMemo(() => {
    const seen = [];
    graph.nodes.forEach((n) => {
      if (n.type === "course" && n.track && !seen.find((t) => t.track === n.track)) {
        seen.push({ track: n.track, label: n.track_label || n.track });
      }
    });
    return seen;
  }, [graph]);

  // Fly the camera to a star the Oracle recommends via /map?focus=<route>
  useEffect(() => {
    const focusTo = searchParams.get("focus");
    if (!focusTo || !graph.nodes.length || !Object.keys(layout).length) return;
    const dec = (s) => { try { return decodeURIComponent(s || "").toLowerCase().trim(); } catch { return (s || "").toLowerCase().trim(); } };
    const target = dec(focusTo);
    const path = target.split("?")[0];
    let node =
      graph.nodes.find((n) => dec(n.to) === target) ||
      graph.nodes.find((n) => dec(n.to).startsWith(target) || target.startsWith(dec(n.to))) ||
      graph.nodes.find((n) => dec((n.to || "").split("?")[0]) === path);
    if (node && layout[node.id]) {
      const id = node.id;
      const t = setTimeout(() => {
        setSelected(id); setHover(id); centerOn(layout[id], 2.2);
        setSearchParams({}, { replace: true });
      }, 400);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, layout, searchParams]);

  const goNode = (n) => {
    if (moved.current >= 10) return;
    if (n.type === "code" && Array.isArray(n.children) && n.children.length) {
      toggleExpand(n.id);
      const p = layout[n.id];
      if (p) centerOn(p, Math.max(view.scale, 1.5));
      return;
    }
    if (focusMode && n.type === "course" && focused !== n.id) {
      setFocused(n.id);
      const p = layout[n.id];
      if (p) centerOn(p, Math.max(view.scale, 1.6));
      return;
    }
    navigate(n.to);
  };

  const toggleFocusMode = () => {
    setFocusMode((v) => {
      if (v) setFocused(null);
      return !v;
    });
  };

  const matches = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return [];
    return graph.nodes.filter((n) => isVisible(n) && (n.label || "").toLowerCase().includes(s)).slice(0, 7);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, graph, disabled]);

  const flyTo = (n) => {
    const p = layout[n.id];
    if (p) { stopFly(); setSelected(n.id); setHover(n.id); centerOn(p, 2); }
    setSearch("");
  };
  const connected = useMemo(() => {
    const focus = focused || selected || hover;
    if (!focus) return new Set();
    const s = new Set([focus]);
    graph.edges.forEach((e) => {
      if (e.source === focus) s.add(e.target);
      if (e.target === focus) s.add(e.source);
    });
    return s;
  }, [focused, selected, hover, graph]);

  // Expanded code-hub children fanned around their hub star.
  const extras = useMemo(() => {
    const out = [];
    graph.nodes.forEach((h) => {
      if (h.type !== "code" || !expanded.has(h.id)) return;
      const hub = layout[h.id];
      if (!hub) return;
      const kids = h.children || [];
      const perRing = 14;
      kids.forEach((c, i) => {
        const ring = Math.floor(i / perRing);
        const inRing = i % perRing;
        const count = Math.min(perRing, kids.length - ring * perRing);
        const radius = 68 + ring * 48;
        const ang = (inRing / count) * Math.PI * 2 + ring * 0.34 - Math.PI / 2;
        out.push({ ...c, parent: h.id, x: hub.x + radius * Math.cos(ang), y: hub.y + radius * Math.sin(ang) });
      });
    });
    return out;
  }, [graph, expanded, layout]);

  const tip = hover || focused || selected;

  return (
    <PageShell>
      <style>{`@keyframes twinkle{0%,100%{opacity:.25}50%{opacity:1}}
        .twk{animation:twinkle 4s ease-in-out infinite}`}</style>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <Waypoints className="w-7 h-7 text-primary" />
          <h1 className="font-serif text-5xl">The Rabbit Hole</h1>
        </div>
        <p className="text-muted-foreground max-w-2xl mb-6">
          A constellation of everything you're studying — each course a star, wired to the cases, terms, statutes,
          and state law that give it teeth. Drag (or one finger) to fly through space, pinch or scroll to zoom, and
          tap any star to warp straight in.
        </p>

        <div className="flex flex-wrap items-center gap-2.5 mb-4 text-xs font-mono" data-testid="map-legend">
          {[
            { key: "foundations", label: "foundations", color: TRACK_COLORS.foundations },
            { key: "status", label: "status", color: TRACK_COLORS.status },
            { key: "consumer", label: "consumer", color: TRACK_COLORS.consumer },
            { key: "case", label: "case", color: TYPE_COLORS.case },
            { key: "term", label: "term", color: TYPE_COLORS.term },
            { key: "statute", label: "statute", color: TYPE_COLORS.statute },
            { key: "code", label: "codes & rules", color: TYPE_COLORS.code },
            { key: "deck", label: "flashcards", color: TYPE_COLORS.deck },
            { key: "portal", label: "sections", color: TYPE_COLORS.portal },
            { key: "other", label: "state / other", color: TYPE_COLORS.state },
            { key: "teaching", label: "teachings", color: TYPE_COLORS.teaching },
          ].map(({ key, label, color }) => {
            const off = disabled.has(key);
            return (
              <button key={key} onClick={() => toggleFilter(key)} data-testid={`legend-filter-${key}`}
                aria-pressed={!off}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-all ${off ? "border-white/10 text-muted-foreground/40 line-through" : "border-white/15 text-muted-foreground hover:text-foreground hover:border-white/30"}`}>
                <span className="w-3 h-3 rounded-full" style={{ background: color, boxShadow: off ? "none" : `0 0 6px ${color}`, opacity: off ? 0.3 : 1 }} /> {label}
              </button>
            );
          })}
          {disabled.size > 0 && (
            <button onClick={() => setDisabled(new Set())} data-testid="legend-filter-reset"
              className="px-2 py-1 text-primary hover:underline">reset</button>
          )}
        </div>

        <div ref={containerRef} data-testid="rabbit-hole-map"
          className="relative overflow-hidden rounded-lg border border-[#23233a]"
          style={{
            height: "72vh", touchAction: "none", cursor: pointers.current.size ? "grabbing" : "grab",
            background: "radial-gradient(ellipse at 22% 28%, rgba(120,70,180,0.22), transparent 55%), radial-gradient(ellipse at 78% 72%, rgba(40,110,170,0.20), transparent 55%), radial-gradient(circle at 50% 45%, #0c0c1a 0%, #06060d 70%, #040408 100%)",
          }}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove}
          onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onPointerLeave={onPointerUp}
          onWheel={onWheel}>

          {/* deep-space starfield (fixed) */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
            {bgStars.map((s, i) => (
              <circle key={i} cx={s.x} cy={s.y} r={s.r / 8} fill="#fffdf5" opacity={s.o}
                className="twk" style={{ animationDelay: `${s.d}s` }} />
            ))}
          </svg>

          {/* star search */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 w-64 max-w-[70%]"
            onPointerDown={(e) => e.stopPropagation()} onWheel={(e) => e.stopPropagation()}>
            <div className="relative">
              <Search className="w-4 h-4 text-white/50 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                data-testid="map-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && matches[0]) flyTo(matches[0]); if (e.key === "Escape") setSearch(""); }}
                placeholder="Search stars…"
                className="w-full bg-black/60 backdrop-blur border border-white/15 rounded-full pl-8 pr-3 py-1.5 text-xs text-white placeholder-white/40 outline-none focus:border-primary"
              />
            </div>
            {matches.length > 0 && (
              <div className="mt-1.5 bg-black/80 backdrop-blur border border-white/10 rounded-sm overflow-hidden" data-testid="map-search-results">
                {matches.map((m) => (
                  <button key={m.id} onClick={() => flyTo(m)} data-testid={`map-search-result-${m.id.replace(/[^a-z0-9]+/gi, "-")}`}
                    className="w-full text-left px-3 py-1.5 text-xs text-white/85 hover:bg-white/10 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: m.type === "course" ? (TRACK_COLORS[m.track] || "#ffd873") : (TYPE_COLORS[m.type] || "#c9c9d6") }} />
                    <span className="truncate">{m.label}</span>
                    <span className="ml-auto text-[9px] uppercase tracking-widest text-white/40 shrink-0">{m.type}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5 bg-black/50 backdrop-blur border border-white/10 rounded-sm p-2" data-testid="trail-replay">
            <span className="text-[9px] uppercase tracking-widest font-mono text-white/60 px-1">Guided path</span>
            {tracks.map((t) => (
              <button key={t.track} onClick={() => playTrack(t.track)} data-testid={`trail-play-${t.track}`}
                className="flex items-center gap-1.5 text-[11px] text-white/85 hover:text-primary px-1.5 py-1 rounded-sm transition-colors text-left">
                <span className="w-2 h-2 rounded-full" style={{ background: TRACK_COLORS[t.track] || "#ffd873" }} /> {t.label}
              </button>
            ))}
            {flying && (
              <button onClick={stopFly} data-testid="trail-stop" className="text-[11px] text-amber-300 hover:text-amber-200 px-1.5 py-1 text-left">■ Stop</button>
            )}
          </div>

          <div className="absolute top-3 right-3 z-10 flex flex-col gap-2">
            <button onClick={() => btnZoom(1.2)} data-testid="map-zoom-in" className="w-9 h-9 flex items-center justify-center bg-white/5 backdrop-blur border border-white/10 rounded-sm text-white/80 hover:text-primary"><ZoomIn className="w-4 h-4" /></button>
            <button onClick={() => btnZoom(0.83)} data-testid="map-zoom-out" className="w-9 h-9 flex items-center justify-center bg-white/5 backdrop-blur border border-white/10 rounded-sm text-white/80 hover:text-primary"><ZoomOut className="w-4 h-4" /></button>
            <button onClick={reset} data-testid="map-zoom-reset" className="w-9 h-9 flex items-center justify-center bg-white/5 backdrop-blur border border-white/10 rounded-sm text-white/80 hover:text-primary"><Maximize2 className="w-4 h-4" /></button>
            <button onClick={toggleFocusMode} data-testid="map-focus-toggle" title="Focus mode: isolate one course and everything it cites"
              className={`w-9 h-9 flex items-center justify-center backdrop-blur border rounded-sm transition-colors ${focusMode ? "bg-primary/25 border-primary text-primary" : "bg-white/5 border-white/10 text-white/80 hover:text-primary"}`}>
              <Crosshair className="w-4 h-4" />
            </button>
          </div>

          {focusMode && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-black/70 backdrop-blur border border-primary/40 px-3 py-1.5 rounded-full text-xs" data-testid="map-focus-bar"
              onPointerDown={(e) => e.stopPropagation()}>
              {focused && layout[focused] ? (
                <>
                  <Crosshair className="w-3.5 h-3.5 text-primary" />
                  <span className="text-white/90 font-mono">Focusing: <span className="text-primary">{layout[focused].label}</span></span>
                  <button onClick={() => setFocused(null)} data-testid="map-focus-clear" className="text-white/60 hover:text-white ml-1"><X className="w-3.5 h-3.5" /></button>
                </>
              ) : (
                <span className="text-white/70 font-mono flex items-center gap-1.5"><Crosshair className="w-3.5 h-3.5 text-primary" /> Tap a course star to isolate its cases, terms &amp; statutes</span>
              )}
            </div>
          )}

          {/* transformed constellation layer */}
          <div className="absolute inset-0" style={{ transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`, transformOrigin: "0 0", transition: flying ? "transform 1.6s cubic-bezier(0.4,0,0.2,1)" : "none" }}>
            <svg className="w-full h-full select-none" viewBox="0 0 1120 1040" preserveAspectRatio="xMidYMid meet">
              {graph.edges.map((e, i) => {
                const a = layout[e.source], b = layout[e.target];
                if (!a || !b) return null;
                if (!isVisible(a) || !isVisible(b)) return null;
                const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2 - 30;
                const dPath = `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
                const onFocus = focused && (e.source === focused || e.target === focused);
                const onSel = (selected && (e.source === selected || e.target === selected)) || onFocus;
                const active = onSel || (hover && (hover === e.source || hover === e.target));
                return (
                  <g key={i}>
                    <path d={dPath} fill="none"
                      stroke={active ? "#ffd873" : "#9a9ac8"} strokeWidth={active ? 1.8 : 0.7}
                      opacity={active ? 0.9 : ((selected || focused) ? 0.06 : 0.22)}
                      style={active ? { filter: "drop-shadow(0 0 4px #ffd873)" } : undefined} />
                    {onSel && (
                      <>
                        <circle r="4.5" fill="#fff6da" style={{ filter: "drop-shadow(0 0 6px #ffd873)" }}>
                          <animateMotion dur="1.1s" repeatCount="indefinite" path={dPath} rotate="auto" />
                        </circle>
                        <circle r="2.5" fill="#ffd873" opacity="0.8">
                          <animateMotion dur="1.1s" begin="0.35s" repeatCount="indefinite" path={dPath} />
                        </circle>
                      </>
                    )}
                  </g>
                );
              })}
              {!disabled.has("code") && extras.map((c) => {
                const hub = layout[c.parent];
                if (!hub) return null;
                const short = (c.label || "").split("—")[0].trim();
                return (
                  <g key={c.id}>
                    <line x1={hub.x} y1={hub.y} x2={c.x} y2={c.y} stroke={TYPE_COLORS.code} strokeWidth={0.5} opacity={0.35} />
                    <g transform={`translate(${c.x} ${c.y})`} style={{ cursor: "pointer" }}
                      onClick={(ev) => { ev.stopPropagation(); if (moved.current < 10) navigate(c.to); }}
                      onPointerDown={(ev) => ev.stopPropagation()}
                      onMouseEnter={() => setHoverExtra(c)} onMouseLeave={() => setHoverExtra(null)}
                      data-testid={`map-node-${c.id.replace(/[^a-z0-9]+/gi, "-")}`}>
                      <circle r="13" fill="transparent" />
                      <circle r={hoverExtra?.id === c.id ? 6 : 4.5} fill={TYPE_COLORS.code} stroke="#fffdf5" strokeWidth="0.5"
                        style={{ filter: `drop-shadow(0 0 ${hoverExtra?.id === c.id ? 9 : 5}px ${TYPE_COLORS.code})`, transition: "all .15s ease" }} />
                      <text x={0} y={-8} textAnchor="middle" fontSize={8} fontFamily="monospace" fill="#e7e7f2"
                        style={{ pointerEvents: "none", textShadow: "0 0 4px #000" }}>{short}</text>
                    </g>
                  </g>
                );
              })}
              {graph.nodes.map((n) => {
                const p = layout[n.id];
                if (!p) return null;
                if (!isVisible(n)) return null;
                const isCourse = n.type === "course";
                const isHub = n.type === "code";
                const hubOpen = isHub && expanded.has(n.id);
                const color = isCourse ? (TRACK_COLORS[n.track] || "#ffd873") : (TYPE_COLORS[n.type] || TYPE_COLORS.link);
                const outer = isCourse ? 17 : (isHub ? 13 : 10);
                const lit = hover === n.id || connected.has(n.id);
                const focusActive = focused || selected;
                const dim = focusActive && !connected.has(n.id);
                const dimOpacity = focused ? 0.1 : 0.3;
                const big = lit ? 1.35 : 1;
                const label = isCourse ? n.label : n.label.replace(/^(Next|Then|Back to|Case|Term|Dictionary|State|Templates|Ask.*)[:]?\s*/i, "");
                return (
                  <g key={n.id} transform={`translate(${p.x} ${p.y})`} style={{ cursor: "pointer", opacity: dim ? dimOpacity : 1, transition: "opacity .2s ease" }}
                    onClick={(ev) => { ev.stopPropagation(); goNode(n); }}
                    onPointerDown={(ev) => ev.stopPropagation()}
                    onMouseEnter={() => setHover(n.id)} onMouseLeave={() => setHover(null)}
                    data-testid={`map-node-${n.id.replace(/[^a-z0-9]+/gi, "-")}`}>
                    <circle r={hubOpen ? outer * 1.5 : outer * 2.1} fill="transparent" />
                    <polygon
                      points={starPolygon(hubOpen ? 6 : 5, outer * big, outer * 0.42 * big)}
                      fill={color} stroke={hubOpen ? "#fff6da" : "#fffdf5"} strokeWidth={hubOpen ? 1.1 : 0.6}
                      style={{ filter: `drop-shadow(0 0 ${lit || hubOpen ? 14 : 7}px ${color})`, transition: "all .18s ease" }} />
                    <text x={0} y={outer + (isCourse ? 16 : 13)} textAnchor="middle"
                      fontSize={isCourse ? 13 : 10} fontFamily={isCourse ? "Georgia, serif" : "monospace"}
                      fill="#e7e7f2" style={{ pointerEvents: "none", textShadow: "0 0 4px #000" }}>
                      {label.length > 26 ? label.slice(0, 24) + "…" : label}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>

          {(hoverExtra || (tip && layout[tip])) && (() => {
            const d = hoverExtra || layout[tip];
            return (
              <div className="absolute bottom-3 left-3 max-w-md bg-black/70 backdrop-blur border border-white/10 px-4 py-3 rounded-sm text-sm z-10" data-testid="map-tooltip">
                <span className="text-primary font-mono text-xs uppercase tracking-widest">{d.type === "code" ? "code index" : d.type}</span>
                <p className="mt-1 text-white">{d.label}</p>
                {d.note && <p className="text-white/60 text-xs mt-0.5">{d.note}</p>}
                <div className="mt-2 flex items-center gap-4">
                  <button onClick={() => (d.type === "code" ? toggleExpand(d.id) : navigate(d.to))} data-testid="map-open"
                    className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-primary hover:underline">
                    {d.type === "code" ? (expanded.has(d.id) ? "Collapse ▾" : "Expand ▸") : d.type === "teaching" ? "Open source path →" : "Open →"}
                  </button>
                  {d.external && (
                    <a href={d.external} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-white/70 hover:text-primary">
                      Cornell LII →
                    </a>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
        <p className="text-xs text-muted-foreground font-mono mt-4">Tip: tap a star to warp straight to that course, case, term or source. Pinch to magnify, drag to travel.</p>
      </div>
    </PageShell>
  );
}
